// Performance Overview domain + wire types (contract 27 §5).
//
// The saved-view pair landed with the scaffold (ORP-18947); the pivot/toolbar types below land
// with the Budget & Performance model cluster (ORP-18950), the first code that produces them.

/** v3 `PerformanceOverviewSettings.vue:115–116` defaults: 'Daily' / 'Table'. */
export type OverviewFrequency = 'daily' | 'weekly' | 'monthly'
export type OverviewViewType = 'table' | 'dateTable' | 'breakdownTable' | 'campaignLabel'

/** The saved view as the APP models it — camelCase, lowercase enums. */
export interface SavedOverviewView {
  frequency: OverviewFrequency
  viewType: OverviewViewType
  /** `marketing_channel` values. */
  channels: string[]
  /** `ReportColumn.field` values. */
  metrics: string[]
  /** `OverviewGroup.field` values. */
  groups: string[]
}

/**
 * ⚠️ The saved view as the SERVER stores it — the JSON blob inside the generic `mediaColumns`
 * column of `POST /upsert_table_view_config` (`table_id: 'perf-overview-save'`).
 *
 * This is WIRE DATA, not the domain type. v3 writes snake_case `view_type` with DISPLAY-STRING
 * enum values (`PerformanceOverviewArea.vue:249–255`) and reads them back with a raw `JSON.parse`
 * (`PerformanceOverviewSettings.vue:208–210`). Rows already exist in production with this shape and
 * v3 keeps writing it for the whole strangler window, so v4 preserves its snake_case wire shape.
 * The retired `Ad Group Label` token remains readable but normalizes to the supported `table`
 * view; writers never emit it. Serialising the domain object directly would emit
 * `{"viewType":"table"}` and silently orphan every existing saved view. There is no version field
 * and no migration (BE-27-01).
 */
export interface SavedOverviewViewDto {
  frequency: 'Daily' | 'Weekly' | 'Monthly'
  /** `Ad Group Label` is a legacy input accepted by the parser and normalized to `table`. */
  view_type: 'Table' | 'Date Table' | 'Breakdown Table' | 'Campaign Label' | 'Ad Group Label'
  channels: string[]
  metrics: string[]
  groups: string[]
}

/** The `table_id` the blob is upserted under (v3 `PerformanceOverviewArea.vue:246`). */
export const OVERVIEW_VIEW_TABLE_ID = 'perf-overview-save'

/** One grouping dimension the toolbar offers (v3 `PerformanceOverviewArea.vue:129–133` rows). */
export interface OverviewGroup {
  field: string
  header: string
}

/** One channel-group entry the pivot derives (v3's `updateChannels` payload rows, `:388–398`). */
export interface OverviewChannel {
  /** Lowercased joined group value — the row identity across the totals maps. */
  key: string
  marketing_channel: string
  group_value: string
}

/** One pivoted channel-group row (v3 `processData`'s per-group accumulator, minus `dataMap`). */
export interface OverviewGroupRow {
  key: string
  marketing_channel: string
  group_value: string
  /** Field names observed in this group's rows — the grids intersect them with the selection. */
  metrics: string[]
  /** One row per date bucket (backfilled) or per custom-group label. */
  data: Record<string, unknown>[]
}

/** The pure `pivot()` output — everything v3 derived across 6 refs in `processData`. */
export interface OverviewPivot {
  groups: OverviewGroupRow[]
  /** Per-bucket cross-group totals (v3 `apiDataTotal`), sorted newest-first in date views. */
  dateTotals: Record<string, unknown>[]
  /** Longest row axis (v3 `arrLength`) — the backfilled bucket count in date views. */
  rowCount: number
  /** Channel-group option list in display-rank order (v3's `updateChannels` emission). */
  channels: OverviewChannel[]
}

/**
 * The FOREIGN slice of the shared `perf-overview-save` row — every field of module 08's
 * `ReportViewConfig` except `tableId` (structurally compatible, so 08's read row satisfies it
 * without this feature re-exporting 08's internal type).
 *
 * Why it exists: `upsert_table_view_config` upserts the WHOLE row and blanks absent fields, so a
 * writer that sends only its own slice wipes every co-writer of the same `table_id` (v3 has this
 * live). `SavedViewApi.upsertOverviewView` therefore REQUIRES the current row and echoes these
 * fields back — read-merge-write, never write-only.
 */
export interface OverviewViewRow {
  dimensions: string[]
  metrics: string[]
  sortField: string | null
  sortOrder: string | null
  breakdownColumn: string | null
  mediaColumns: string[] | string | null
  lineMetric: string | null
  checkLine: string | null
  activeChart: string | null
  groupColumn: string | null
}
